import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermisos } from '../../context/PermisosContext';
import { Search } from 'lucide-react';
import ObrasTable from './ObrasTable';
import ObraEditar from './ObraEditar';
import ObraNueva from './ObraNueva';

const ObrasContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState('');
  const [estadoObraFilter, setEstadoObraFilter] = useState('');
  const [estadoOfertaFilter, setEstadoOfertaFilter] = useState('');
  const [segmentoFilter, setSegmentoFilter] = useState('');
  const [ciudadFilter, setCiudadFilter] = useState('');
  const [zonaFilter, setZonaFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedObra, setSelectedObra] = useState(null);
  const [showNuevaObra, setShowNuevaObra] = useState(false);
  const [nuevaObra, setNuevaObra] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { user, loadingPermisos, setObras, comercialIdsPermitidos, ubicacionesPermitidas, idsBloquesPermitidos, obras, loadingObras, infoComerciales, refreshObras, idsSeccionesPermitidos, refreshVisitas, tieneAccesoObras } = usePermisos();
  
  // Filtro de Obras
  // const filteredObras = obras.filter(v => {
  //   const search = searchTerm.toLowerCase();
  //   const clienteMatch = (v.Cliente || '').toLowerCase().includes(search);
  //   const comercialMatch = (v.Comercial || '').toLowerCase().includes(search);
  //   const ofertaMatch = (v.Oferta || '').toLowerCase().includes(search);
  //   const idComercialMatch = (v.IdComercial ? String(v.IdComercial) : '').toLowerCase().includes(search);
  //   const idClienteMatch = (v.IdCliente ? String(v.IdCliente) : '').toLowerCase().includes(search);
  //   const idOfertaMatch = (v.IdOferta ? String(v.IdOferta) : '').toLowerCase().includes(search);
  //   const constructoraMatch = (v.Constructora || '').toLowerCase().includes(search);
  //   const jefeObraMatch = (v.JefeObra || '').toLowerCase().includes(search);
  //   const textMatch = comercialMatch || constructoraMatch || jefeObraMatch || clienteMatch || ofertaMatch || idComercialMatch || idClienteMatch || idOfertaMatch;
  //   const estadoObraMatch = estadoObraFilter ? v.EstadoObra === estadoObraFilter : true;
  //   const estadoOfertaMatch = estadoOfertaFilter ? v.EstadoOferta === estadoOfertaFilter : true;
  //   const segmentoMatch = segmentoFilter ? v.Segmento === segmentoFilter : true;
  //   return textMatch && estadoObraMatch && estadoOfertaMatch && segmentoMatch;
  // });

  const filteredObras = obras.filter(v => {
    const search = (searchTerm || filter).toLowerCase();
    // Unir todos los valores de la obra en un solo string
    const allValues = Object.values(v).join(' ').toLowerCase();
    const textMatch = !search || allValues.includes(search);
    const estadoObraMatch = estadoObraFilter ? v.EstadoObra === estadoObraFilter : true;
    const estadoOfertaMatch = estadoOfertaFilter ? v.EstadoOferta === estadoOfertaFilter : true;
    const segmentoMatch = segmentoFilter ? v.Segmento === segmentoFilter : true;
    const zonaMatch = zonaFilter ? v.Subdivision === zonaFilter : true;
    const ciudadMatch = ciudadFilter ? v.Poblacion === ciudadFilter : true;
    return textMatch && estadoObraMatch && estadoOfertaMatch && segmentoMatch && zonaMatch && ciudadMatch;
  });

  const filtrosActivos =
    filter.trim() !== '' ||
    searchTerm.trim() !== '' ||
    estadoObraFilter !== '' ||
    estadoOfertaFilter !== '' ||
    segmentoFilter !== '' ||
    ciudadFilter !== '' ||
    zonaFilter !== ''
  ;

  // Opciones únicas para los selects de filtro
  const estadoOfertaOptions = Array.from(new Set(obras.map(v => v.EstadoOferta).filter(Boolean)));
  const segmentoOptions = Array.from(new Set(obras.map(v => v.Segmento).filter(Boolean)));
  const ciudadOptions = Array.from(new Set(obras.map(v => v.Poblacion).filter(Boolean)));
  const zonaOptions = Array.from(new Set(obras.map(v => v.Subdivision).filter(Boolean)));

  // Función para ejecutar la búsqueda
  const handleSearch = () => {
    setSearchTerm(filter);
    setCurrentPage(0);
  };

  // Función para manejar Enter en el input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // useEffect para detectar si se debe abrir una obra específica desde la URL
  useEffect(() => {
    const idObraParam = searchParams.get('openObra');
    if (idObraParam && obras.length > 0 && !selectedObra) {
      const idObra = parseInt(idObraParam, 10);
      const obraToOpen = obras.find(o => o.IdObra === idObra);
      if (obraToOpen) {
        // 🔥 Mapear Seccion -> Delegacion para que los dropdowns funcionen
        const obraMapeada = {
          ...obraToOpen,
          Delegacion: obraToOpen.Seccion || obraToOpen.Delegacion,
          Subdivision: obraToOpen.Subdivision
        };
        setSelectedObra(obraMapeada);
        // Limpiar el parámetro de la URL después de abrir
        setSearchParams({});
      }
    }
  }, [searchParams, obras, selectedObra, setSearchParams]);

  // Modal edición
  const openModal = (obra) => {
    // console.log('🔍 Obra original del backend:', obra);
    // console.log('🔍 Todos los campos de la obra:', Object.keys(obra));
    // console.log('🔍 Subdivision:', obra.Subdivision);
    // console.log('🔍 Seccion:', obra.Seccion);
    // console.log('🔍 Delegacion:', obra.Delegacion);
    
    // 🔥 Mapear Seccion -> Delegacion para que DelegacionDropDown funcione
    const obraMapeada = {
      ...obra,
      Delegacion: obra.Seccion || obra.Delegacion, // Mapear del backend al frontend
      Subdivision: obra.Subdivision // Ya debería venir del backend
    };
    
    // console.log('✅ Obra mapeada enviada a ObraEditar:', obraMapeada);
    // console.log('✅ Subdivision en obra mapeada:', obraMapeada.Subdivision);
    // console.log('✅ Delegacion en obra mapeada:', obraMapeada.Delegacion);
    setSelectedObra(obraMapeada);
  };

  // Modal creación
  const openCrearModal = () => {
    setNuevaObra({
      // Información Comercial
      IdComercial: null,
      FechaVisita: new Date().toISOString().slice(0, 10),
      TecnicoComercial: '',
      IdSeccion: null,
      Poblacion: '',
      Subdivision: '',
      IdUbicacion: null,
      ObservacionesC: '',
      
      // Información de la Obra
      IdConstructora: null,
      Obra: '',
      
      // Contacto Visitado
      NombreCV: '',
      TelefonoCV: '',
      EmailCV: '',
      
      // Dirección de la obra
      PoblacionObra: '',
      CalleObra: '',
      NumeroObra: '',
      
      // Estado y Tipo
      EstadoObra: '',
      TipoMaterial: '',
      ObservacionesCV: '',
      IdTipoObra: null,
      TipoObraNombre: '',
      TipoObraInfoExtra: '',
      
      // Otros campos opcionales
      IdCliente: null,
      IdSegmento: null,
      JefeObra: '',
      TelefonoC: '',
      Ofertado: null,
      NOferta: '',
      Otros: '',
      
      // Ubicación del mapa
      Direccion: '',
      LatLong: '',
      Latitud: null,
      Longitud: null,
      LatLong: '',
      Latitud: null,
      Longitud: null,
    });
    setShowNuevaObra(true);
  };



  // Callback después de crear obra exitosamente
  // ObraNueva ya maneja todo el guardado (obra + subcontratas)
  const handleAddObra = () => {
    setShowNuevaObra(false);
    refreshObras(); // Refresca la lista completa de obras
  };

  // 🔐 Si el usuario NO tiene acceso a obras (DESPUÉS de todos los hooks)
  if (!tieneAccesoObras && !loadingPermisos) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="text-center border rounded-xl p-8">
          <div className="mb-4">
            <svg className="w-20 h-20 mx-auto text-red-400 sm:w-24 sm:h-24" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Acceso Denegado</h2>
          <p className="text-gray-600 mb-2 text-sm">
            Tu usuario no tiene permisos para acceder a la sección de <strong>Obras</strong>.
          </p>
          <p className="text-xs text-gray-500 mb-6">
            Si crees que esto es un error o necesitas acceso, contacta con el equipo de IT.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-3 py-1.5 rounded-lg text-red-600 border bg-red-50 hover:bg-red-100 border-red-200 text-sm font-medium transition-colors duration-200"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {selectedObra ? (
        <div>
          <ObraEditar
            obra={selectedObra}
            setObra={setSelectedObra}
            onSave={() => {
              // Cerrar modal y refrescar lista
              setSelectedObra(null);
              refreshObras();
            }}
            onCancel={() => setSelectedObra(null)}
            user={user}
            comercialIdsPermitidos={comercialIdsPermitidos}
            ubicacionesPermitidas={ubicacionesPermitidas}
            idsSeccionesPermitidos={idsSeccionesPermitidos} 
            infoComerciales={infoComerciales}
            refreshObras={refreshObras}
            refreshVisitas={refreshVisitas}
          />
        </div>
      ) : showNuevaObra ? (
        <div>
          <ObraNueva
            obra={nuevaObra}
            setObra={setNuevaObra}
            onSave={handleAddObra}
            onCancel={() => setShowNuevaObra(false)}
            user={user}
            comercialIdsPermitidos={comercialIdsPermitidos}
            ubicacionesPermitidas={ubicacionesPermitidas}
            infoComerciales={infoComerciales}
            refreshObras={refreshObras}
            idsSeccionesPermitidos={idsSeccionesPermitidos} 


          />
        </div>
      ) : (
        // <div className="p-4 sm:p-6 space-y-6 bg-gray-50 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-6  bg-gray-50 no-select min-h-screen">
          <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row gap-4 my-4 sm:gap-0 sm:justify-between sm:items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Obras</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Gestión de obras y proyectos</p>
            </div>
            <div className="flex items-center gap-3 bg-white px-3 sm:px-4 py-2 sm:py-3 rounded-xl shadow-sm border hover:shadow-lg hover:border-blue-200 transition-all duration-300 cursor-pointer">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-800 text-xs sm:text-sm md:text-base truncate max-w-[100px] sm:max-w-[150px] md:max-w-none">
                  {user?.name || 'Usuario'}
                </p>
              </div>
            </div>
          </div>

          {/* Filtros y acciones */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-4 my-4">
            <div className="flex flex-col gap-4">

              {/* Filtros principales */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 items-stretch sm:items-end flex-1">
                {/* Búsqueda */}
                <div className="flex flex-col w-full sm:w-auto">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar</label>
                  <div className="flex w-full sm:w-64 ">
                    <input
                      type="text"
                      placeholder="Buscar en todas las columnas..."
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="px-3 sm:px-4 py-2 border border-gray-200 rounded-l-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 flex-1 sm:flex-none border-r-0 text-sm"
                    />
                    <button
                      onClick={handleSearch}
                      className="px-2 sm:px-3 py-1.5 bg-blue-600 text-white rounded-r-xl hover:bg-blue-700 transition-all duration-200 shadow-sm border border-blue-600 border-l-0 flex-shrink-0"
                      title="Buscar"
                    >
                      <Search size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                  </div>
                </div>

                {/* Filtros dropdown - Grid responsive */}
                <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4 w-full sm:w-auto">
                  <DropdownGroup 
                    label="Estado Obra" 
                    options={Array.from(new Set(obras.map(v => v.EstadoObra).filter(Boolean)))}
                    value={estadoObraFilter} 
                    onChange={value => {
                      setEstadoObraFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todas" 
                  />

                  {/* <DropdownGroup 
                    label="Estado Oferta" 
                    options={estadoOfertaOptions}
                    value={estadoOfertaFilter} 
                    onChange={value => {
                      setEstadoOfertaFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todas" 
                  /> */}

                  {/* <DropdownGroup 
                    label="Segmento" 
                    options={segmentoOptions}
                    value={segmentoFilter} 
                    onChange={value => {
                      setSegmentoFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todos" 
                  /> */}

                  <DropdownGroup 
                    label="Zona" 
                    options={ciudadOptions}
                    value={ciudadFilter} 
                    onChange={value => {
                      setCiudadFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todas" 
                  />

                  <DropdownGroup 
                    label="Ciudad" 
                    options={zonaOptions}
                    value={zonaFilter} 
                    onChange={value => {
                      setZonaFilter(value);
                      setCurrentPage(0);
                    }}
                    placeholder="Todas" 
                  />
                </div>

                {filtrosActivos && (
                  <div className="flex justify-center sm:justify-start w-full sm:w-auto">
                    <button
                      onClick={() => {
                        setFilter(''); 
                        setSearchTerm('');
                        setEstadoObraFilter(''); 
                        setEstadoOfertaFilter('');
                        setSegmentoFilter('');
                        setCiudadFilter('');
                        setZonaFilter('');
                        setCurrentPage(0);
                      }}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-all duration-200 border border-red-200 text-sm w-full sm:w-auto"
                      title="Limpiar filtros"
                    >
                      Limpiar filtros
                    </button>
                  </div>
                )}
  

                {/* Acciones y contador */}
                <div className='justify-between'>
                  <div className="grid grid-cols-2 sm:flex gap-3 sm:gap-4 w-full sm:w-auto sm:items-center justify-end pt-3 sm:pt-0 border-t sm:border-t-0">
                    {/* Contador */}
                    <div className="flex justify-center sm:justify-start">
                      <span className="px-3 py-2 bg-green-100 text-green-800 rounded-xl text-sm font-semibold border border-green-200 w-full sm:w-auto text-center">
                        {filteredObras.length} obras
                      </span>
                    </div>

                    {/* Botón nueva obra */}
                    <button
                      onClick={openCrearModal}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all duration-200 shadow-sm border border-blue-600 w-full sm:w-auto"
                      title="Crear nueva visita"
                    >
                      + Nueva Obra
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {loadingPermisos || loadingObras ? (
            <div className="flex flex-col justify-center items-center py-32">
              <div className="loader border-t-4 border-red-500 rounded-full w-12 h-12 animate-spin mb-4"></div>
              <p className="text-gray-600">Cargando obras...</p>
            </div>
          ) : obras.length === 0 ? (
            <div className='flex justify-center mt-5'>
              <p className="text-center mt-5 bg-gray-200 rounded-xl text-gray-800 border-gray-300 w-max p-2">No se encontraron obras en la base de datos.</p>
            </div>
          ) : filteredObras.length > 0 ? (
            <ObrasTable 
              obras={filteredObras} 
              openModal={openModal} 
              setCurrentPage={setCurrentPage} 
              currentPage={currentPage} 
            />
          ) : (
            <div className='flex justify-center mt-5'>
              <p className="text-center mt-5 bg-gray-200 rounded-xl text-gray-800 border-gray-300 w-max p-2">Ninguna obra coincide con el filtro.</p>
            </div>
          )}
          </div>
        </div>
      )}
    </>
  );
};

function DropdownGroup({ label, options, value, onChange, placeholder }) {
  return (
    <div className="flex flex-col min-w-0 w-full sm:min-w-[140px] sm:w-auto">
      <label className="text-xs sm:text-sm font-medium text-gray-700 mb-1 truncate">{label}</label>
      <DropdownFilter options={options} value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

function DropdownFilter({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-200 rounded-xl bg-white text-left flex justify-between items-center hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
        onClick={() => setOpen(o => !o)}
      >
        <span className="truncate block max-w-[70%] sm:max-w-[80%] text-xs sm:text-sm" title={value || placeholder}>
          {value || placeholder}
        </span>
        <svg 
          className={`w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2 text-gray-500 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul className="absolute z-30 mt-1 min-w-full w-max bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto break-words">
          <li
            className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${!value ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            {placeholder}
          </li>
          {options.map(opt => (
            <li
              key={opt}
              className={`cursor-pointer px-4 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors ${value === opt ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ObrasContent;